import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "../utils/axiosInstance";

const fmt = (d) => d ? new Date(d).toLocaleString("rw-RW", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const STATUS_BADGE = {
  sent_to_village: { bg: "rgba(0,71,171,0.1)", color: "#0047AB", border: "rgba(0,71,171,0.25)", label: "🏘️ Yoherejwe ku Mudugudu" },
  sent_to_cell:    { bg: "rgba(124,58,237,0.1)", color: "#7c3aed", border: "rgba(124,58,237,0.25)", label: "🏛️ Yoherejwe kuri Kagari" },
};

const EMPTY = {
  visitor_name: "", visitor_phone: "", visitor_national_id: "", visitor_cell: "", visitor_village: "",
  visitor_sector: "", visitor_district: "", visited_person_name: "",
  visited_person_phone: "", visited_person_id: "",
};

const Field = ({ label, name, value, onChange, placeholder, required }) => (
  <div>
    <label style={{ display:"block", fontSize:"0.82rem", fontWeight:700, color:"#64748b", marginBottom:5 }}>{label}</label>
    <input type="text" name={name} value={value} onChange={onChange} placeholder={placeholder} required={required}
      style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1px solid #e2e8f0", background:"#f8fafc", color:"#0d1b2a", fontSize:"0.92rem", outline:"none", boxSizing:"border-box" }} />
  </div>
);

export default function AbashyitsiIsibo() {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [sending, setSending]   = useState(null);
  const [confirm, setConfirm]   = useState(null);
  const [toast, setToast]       = useState(null);
  const [search, setSearch]     = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);

  const notify = (msg, type = "ok") => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  const fetchVisitors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/visitors");
      setVisitors(res.data || []);
    } catch { notify("Habaye ikosa mu gusoma amakuru.", "err"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchVisitors(); }, [fetchVisitors]);

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.visitor_name.trim() || !form.visited_person_name.trim()) {
      notify("Amazina y'uwasuye n'uwasuwe ni ngombwa.", "err"); return;
    }
    setSaving(true);
    try {
      await axios.post("/api/visitors", form);
      notify("✅ Umushyitsi yanditswe neza!");
      setForm(EMPTY); setShowForm(false);
      fetchVisitors();
    } catch (err) {
      notify(err?.response?.data?.error || "Kubika byanze.", "err");
    } finally { setSaving(false); }
  };

  const handleSend = async (v) => {
    setSending(v.id); setConfirm(null);
    try {
      await axios.post("/api/visitors/send-to-village", { visitor_id: v.id });
      notify("✅ Umushyitsi yoherejwe ku Mudugudu neza!");
      fetchVisitors();
    } catch (err) {
      notify(err?.response?.data?.error || "Kohereza byanze.", "err");
    } finally { setSending(null); }
  };

  const filtered = visitors.filter(v =>
    !search ||
    v.visitor_name?.toLowerCase().includes(search.toLowerCase()) ||
    v.visited_person_name?.toLowerCase().includes(search.toLowerCase()) ||
    v.visitor_phone?.includes(search)
  );

  return (
    <div style={{ fontFamily: "Nunito Sans, sans-serif", color: "#0d1b2a" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:20 }}>
        <div>
          <h2 style={{ margin:0, fontSize:"1.15rem", fontWeight:700, color:"#0d1b2a" }}>
            <i className="bi bi-person-walking" style={{ color:"#0047AB", marginRight:8 }} />
            Abashyitsi
          </h2>
          <p style={{ margin:"4px 0 0", fontSize:"0.86rem", color:"#64748b" }}>Ohereza abashyitsi ku Mudugudu</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setShowForm(true); }}
          style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"9px 18px", borderRadius:6, border:"none", background:"#0047AB", color:"#fff", fontWeight:700, fontSize:"0.92rem", cursor:"pointer", whiteSpace:"nowrap" }}>
          <i className="bi bi-plus-lg" /> Ongeraho Umushyitsi
        </button>
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:12, marginBottom:20 }}>
        {[
          { label:"Bose", value:visitors.length, icon:"people-fill", color:"#0047AB" },
          { label:"Ari Imbere", value:visitors.filter(v => !v.transfer_status && v.status?.toLowerCase()==="ari imbere").length, icon:"person-check-fill", color:"#20603d" },
          { label:"Yoherejwe", value:visitors.filter(v => !!v.transfer_status).length, icon:"send-fill", color:"#0369a1" },
        ].map(s => (
          <div key={s.label} style={{ background:"#ffffff", border:"1px solid #e2e8f0", borderLeft:`4px solid ${s.color}`, borderRadius:8, padding:"16px 14px", display:"flex", alignItems:"center", gap:12, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
            <div style={{ width:40, height:40, borderRadius:8, background:`${s.color}18`, color:s.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.2rem" }}>
              <i className={`bi bi-${s.icon}`} />
            </div>
            <div>
              <div style={{ fontSize:"1.65rem", fontWeight:800, color:"#0d1b2a" }}>{s.value}</div>
              <div style={{ fontSize:"0.74rem", color:"#64748b", fontWeight:600 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Search + refresh */}
      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, padding:"0 12px", background:"#ffffff", border:"1px solid #e2e8f0", borderRadius:6, height:36, flex:"1 1 220px", maxWidth:360 }}>
          <i className="bi bi-search" style={{ color:"#94a3b8", fontSize:"0.9rem" }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Shakisha umushyitsi..."
            style={{ border:"none", background:"none", color:"#0d1b2a", fontSize:"0.9rem", outline:"none", width:"100%" }} />
          {search && <button onClick={() => setSearch("")} style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8", padding:0 }}><i className="bi bi-x" /></button>}
        </div>
        <button onClick={fetchVisitors} style={{ padding:"6px 14px", borderRadius:6, border:"1px solid #e2e8f0", background:"#ffffff", color:"#0047AB", fontSize:"0.86rem", fontWeight:600, cursor:"pointer" }}>
          <i className="bi bi-arrow-clockwise" />
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:"40px 0", color:"#64748b" }}>
          <div className="spinner-border spinner-border-sm text-primary" /> <span style={{ marginLeft:10 }}>Gutegereza...</span>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:"center", padding:"48px 20px", background:"#ffffff", borderRadius:8, border:"1px solid #e2e8f0", display:"flex", flexDirection:"column", alignItems:"center", gap:8 }}>
          <i className="bi bi-person-x" style={{ fontSize:"2.5rem", color:"#cbd5e1" }} />
          <div style={{ color:"#64748b" }}>{search ? "Nta mushyitsi wabonetse" : "Nta mushyitsi wanditswe kuri ubu"}</div>
          {!search && (
            <button onClick={() => { setForm(EMPTY); setShowForm(true); }}
              style={{ marginTop:8, display:"inline-flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:6, border:"none", background:"#0047AB", color:"#fff", fontWeight:700, fontSize:"0.9rem", cursor:"pointer" }}>
              <i className="bi bi-plus-lg" /> Ongeraho Umushyitsi wa Mbere
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile card list (below md) — avoids the cramped horizontal table scroll */}
          <div className="d-md-none" style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {filtered.map((v) => {
              const st = STATUS_BADGE[v.transfer_status];
              const canSend = !v.transfer_status;
              return (
                <div key={v.id} style={{ background:"#ffffff", borderRadius:10, border:"1px solid #e2e8f0", boxShadow:"0 1px 4px rgba(0,0,0,0.06)", padding:14 }}>
                  <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8, marginBottom:10 }}>
                    <span style={{ fontWeight:700, fontSize:"0.98rem", color:"#0d1b2a" }}>{v.visitor_name}</span>
                    {st ? (
                      <span style={{ flexShrink:0, padding:"3px 10px", borderRadius:20, fontSize:"0.76rem", fontWeight:700, whiteSpace:"nowrap", background:st.bg, color:st.color, border:`1px solid ${st.border}` }}>{st.label}</span>
                    ) : (
                      <span style={{ flexShrink:0, padding:"3px 10px", borderRadius:20, fontSize:"0.76rem", fontWeight:700, whiteSpace:"nowrap", background:"rgba(32,96,61,0.1)", color:"#20603d", border:"1px solid rgba(32,96,61,0.25)" }}>🟢 Ari Imbere</span>
                    )}
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:6, fontSize:"0.88rem", marginBottom:12 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <i className="bi bi-telephone-fill" style={{ color:"#94a3b8", width:16 }} />
                      <span style={{ color:"#334155" }}>{v.visitor_phone || "—"}</span>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <i className="bi bi-house-fill" style={{ color:"#94a3b8", width:16 }} />
                      <span style={{ color:"#0047AB", fontWeight:600 }}>{v.visited_person_name}</span>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <i className="bi bi-clock-fill" style={{ color:"#94a3b8", width:16 }} />
                      <span style={{ color:"#334155" }}>{fmt(v.check_in)}</span>
                    </div>
                  </div>
                  {canSend ? (
                    <button onClick={() => setConfirm(v)} disabled={sending === v.id}
                      style={{ width:"100%", display:"inline-flex", alignItems:"center", justifyContent:"center", gap:6, padding:"10px 12px", borderRadius:6, border:"none", background:"#0369a1", color:"#fff", fontWeight:700, fontSize:"0.88rem", cursor:"pointer" }}>
                      <i className="bi bi-send-fill" /> Ohereza ku Mudugudu
                    </button>
                  ) : (
                    <div style={{ textAlign:"center", fontSize:"0.85rem", color:"#64748b", fontStyle:"italic" }}>Yoherejwe</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop table (md and up) */}
          <div className="d-none d-md-block" style={{ overflowX:"auto", borderRadius:8, border:"1px solid #e2e8f0", boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"0.9rem" }}>
              <thead>
                <tr style={{ background:"#f8fafc" }}>
                  {["#","Amazina","Telefoni","Uwasuwe","Yinjiye","Status","Kohereza"].map(h => (
                    <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:"0.74rem", fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.06em", whiteSpace:"nowrap", borderBottom:"1px solid #e2e8f0" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((v, i) => {
                  const st = STATUS_BADGE[v.transfer_status];
                  const canSend = !v.transfer_status;
                  return (
                    <tr key={v.id} style={{ borderBottom:"1px solid #e2e8f0", background: i%2===0 ? "#ffffff" : "#f8fafc" }}>
                      <td style={{ padding:"10px 14px", color:"#64748b" }}>{i+1}</td>
                      <td style={{ padding:"10px 14px", fontWeight:600, color:"#0d1b2a" }}>{v.visitor_name}</td>
                      <td style={{ padding:"10px 14px", color:"#64748b" }}>{v.visitor_phone || "—"}</td>
                      <td style={{ padding:"10px 14px", color:"#0047AB" }}>{v.visited_person_name}</td>
                      <td style={{ padding:"10px 14px", color:"#64748b", whiteSpace:"nowrap" }}>{fmt(v.check_in)}</td>
                      <td style={{ padding:"10px 14px" }}>
                        {st ? (
                          <span style={{ padding:"3px 10px", borderRadius:20, fontSize:"0.78rem", fontWeight:700, background:st.bg, color:st.color, border:`1px solid ${st.border}` }}>{st.label}</span>
                        ) : (
                          <span style={{ padding:"3px 10px", borderRadius:20, fontSize:"0.78rem", fontWeight:700, background:"rgba(32,96,61,0.1)", color:"#20603d", border:"1px solid rgba(32,96,61,0.25)" }}>🟢 Ari Imbere</span>
                        )}
                      </td>
                      <td style={{ padding:"10px 14px" }}>
                        {canSend ? (
                          <button onClick={() => setConfirm(v)} disabled={sending === v.id}
                            style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"6px 12px", borderRadius:6, border:"none", background:"#0369a1", color:"#fff", fontWeight:700, fontSize:"0.86rem", cursor:"pointer", whiteSpace:"nowrap" }}>
                            <i className="bi bi-send-fill" /> Ohereza ku Mudugudu
                          </button>
                        ) : (
                          <span style={{ fontSize:"0.82rem", color:"#64748b", fontStyle:"italic" }}>Yoherejwe</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── ADD FORM MODAL ── */}
      {showForm && createPortal(
        <div onClick={() => setShowForm(false)} style={{ position:"fixed", inset:0, background:"rgba(13,27,42,0.6)", backdropFilter:"blur(4px)", WebkitBackdropFilter:"blur(4px)", zIndex:10050, display:"flex", alignItems:"center", justifyContent:"center", padding:"16px" }}>
          <div onClick={e => e.stopPropagation()} style={{ background:"#ffffff", border:"1px solid #e2e8f0", borderRadius:12, maxWidth:680, width:"100%", maxHeight:"90vh", display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,0.25)" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"20px clamp(16px,4vw,24px) 0", flexShrink:0 }}>
              <h3 style={{ margin:0, fontSize:"1.2rem", fontWeight:700, color:"#0d1b2a" }}>
                <i className="bi bi-person-plus-fill" style={{ color:"#0047AB", marginRight:8 }} />
                Ongeraho Umushyitsi
              </h3>
              <button onClick={() => setShowForm(false)} style={{ background:"none", border:"none", color:"#64748b", fontSize:"1.3rem", cursor:"pointer" }}>
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <form onSubmit={handleAdd} style={{ overflowY:"auto", minHeight:0, padding:"20px clamp(16px,4vw,24px)" }}>
              {/* Uwasuye */}
              <div style={{ marginBottom:20, padding:16, borderRadius:8, background:"#f8fafc", border:"1px solid #e2e8f0" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:"0.82rem", fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:14 }}>
                  <i className="bi bi-person-fill" style={{ color:"#0047AB" }} /> Amakuru y'Uwasuye
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:12 }}>
                  <Field label="Amazina *" name="visitor_name" value={form.visitor_name} onChange={handleChange} placeholder="Amazina yose" required />
                  <Field label="Indangamuntu" name="visitor_national_id" value={form.visitor_national_id} onChange={handleChange} placeholder="Indangamuntu" />
                  <Field label="Telefoni" name="visitor_phone" value={form.visitor_phone} onChange={handleChange} placeholder="07X XXX XXXX" />
                  <Field label="Akagari" name="visitor_cell" value={form.visitor_cell} onChange={handleChange} placeholder="Akagari aturukamo" />
                  <Field label="Umudugudu" name="visitor_village" value={form.visitor_village} onChange={handleChange} placeholder="Umudugudu" />
                  <Field label="Umurenge" name="visitor_sector" value={form.visitor_sector} onChange={handleChange} placeholder="Umurenge" />
                  <Field label="Akarere" name="visitor_district" value={form.visitor_district} onChange={handleChange} placeholder="Akarere" />
                </div>
              </div>

              {/* Uwasuwe */}
              <div style={{ marginBottom:0, padding:16, borderRadius:8, background:"#f8fafc", border:"1px solid #e2e8f0" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:"0.82rem", fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:14 }}>
                  <i className="bi bi-house-fill" style={{ color:"#7c3aed" }} /> Amakuru y'Uwasuwe
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:12 }}>
                  <Field label="Amazina y'Uwasuwe *" name="visited_person_name" value={form.visited_person_name} onChange={handleChange} placeholder="Amazina yose" required />
                  <Field label="Telefoni y'Uwasuwe" name="visited_person_phone" value={form.visited_person_phone} onChange={handleChange} placeholder="07X XXX XXXX" />
                  <Field label="Indangamuntu / Reference" name="visited_person_id" value={form.visited_person_id} onChange={handleChange} placeholder="Indangamuntu cyangwa reference" />
                </div>
              </div>
            </form>

            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", padding:"14px clamp(16px,4vw,24px)", borderTop:"1px solid #e2e8f0", flexShrink:0, background:"#ffffff", borderRadius:"0 0 12px 12px" }}>
              <button type="button" onClick={() => setShowForm(false)}
                style={{ flex:"1 1 auto", padding:"12px 16px", borderRadius:6, border:"1px solid #e2e8f0", background:"#ffffff", color:"#64748b", fontWeight:600, cursor:"pointer", fontSize:"0.92rem" }}>
                Reka
              </button>
              <button type="button" disabled={saving} onClick={handleAdd}
                style={{ flex:"1 1 auto", display:"inline-flex", alignItems:"center", justifyContent:"center", gap:6, padding:"12px 20px", borderRadius:6, border:"none", background: saving ? "#94a3b8" : "#0047AB", color:"#fff", fontWeight:700, cursor: saving ? "not-allowed" : "pointer", fontSize:"0.92rem" }}>
                {saving ? <><span className="spinner-border spinner-border-sm" style={{ marginRight:6 }} />Kubika...</> : <><i className="bi bi-check-lg" style={{ marginRight:6 }} />Bika</>}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Confirm Send Modal */}
      {confirm && createPortal(
        <div onClick={() => setConfirm(null)} style={{ position:"fixed", inset:0, background:"rgba(13,27,42,0.6)", backdropFilter:"blur(4px)", zIndex:10050, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:"#ffffff", border:"1px solid #e2e8f0", borderRadius:12, padding:"28px 24px", maxWidth:420, width:"100%", textAlign:"center", boxShadow:"0 20px 60px rgba(0,0,0,0.25)" }}>
            <div style={{ width:60, height:60, borderRadius:"50%", background:"rgba(3,105,161,0.1)", color:"#0369a1", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.75rem", margin:"0 auto 16px" }}>
              <i className="bi bi-send-fill" />
            </div>
            <h3 style={{ color:"#0d1b2a", marginBottom:8, fontSize:"1.15rem" }}>Ohereza ku Mudugudu</h3>
            <p style={{ color:"#64748b", fontSize:"0.95rem", marginBottom:8 }}>Ushaka kohereza uyu mushyitsi:</p>
            <p style={{ fontWeight:700, color:"#0047AB", fontSize:"1rem", marginBottom:20, padding:"8px 12px", background:"rgba(0,71,171,0.08)", borderRadius:6 }}>«{confirm.visitor_name}»</p>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => setConfirm(null)} style={{ flex:1, padding:"10px 16px", borderRadius:6, border:"1px solid #e2e8f0", background:"#ffffff", color:"#64748b", fontWeight:600, cursor:"pointer", fontSize:"0.92rem" }}>Reka</button>
              <button onClick={() => handleSend(confirm)} disabled={sending === confirm?.id}
                style={{ flex:1, padding:"10px 16px", borderRadius:6, border:"none", background:"#0369a1", color:"#fff", fontWeight:800, cursor:"pointer", fontSize:"0.92rem" }}>
                {sending === confirm?.id ? <><span className="spinner-border spinner-border-sm" style={{ marginRight:6 }} />Kohereza...</> : <><i className="bi bi-send-fill" style={{ marginRight:6 }} />Yego, Ohereza</>}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Toast */}
      {toast && createPortal(
        <div style={{ position:"fixed", bottom:90, left:"50%", transform:"translateX(-50%)", padding:"12px 20px", borderRadius:8, display:"flex", alignItems:"center", gap:10, fontSize:"0.92rem", fontWeight:500, zIndex:10050, maxWidth:"90vw", border:"1px solid", boxShadow:"0 6px 24px rgba(0,0,0,0.15)", background: toast.type==="ok" ? "rgba(32,96,61,0.1)" : "rgba(244,63,94,0.1)", borderColor: toast.type==="ok" ? "rgba(32,96,61,0.3)" : "rgba(244,63,94,0.3)", color: toast.type==="ok" ? "#20603d" : "#be123c" }}>
          <i className={`bi bi-${toast.type==="ok" ? "check-circle-fill" : "exclamation-triangle-fill"}`} />
          {toast.msg}
        </div>,
        document.body
      )}
    </div>
  );
}
